/**
 * ボード（カンバン）の面。**読むための面**で、編集はしない。
 *
 * 招かれた人が最初に見たいのは「いま何がどこまで進んでいるか」で、行の並びではない。
 * だからブラウザの入口はここにする（Mac の作法と同じく、カードを押すと中身が開く）。
 *
 * 行の語彙をそのまま流さず、サーバで畳んだものを読む（`/board`・`/card/:id`）。列を全部
 * 運んでも描くのに要らないし、会話の全文はカードを開くまで要らない。
 */

export interface BoardColumn {
  id: string;
  name: string;
}

export interface BoardCard {
  id: string;
  text: string;
  note: string;
  statusId: string;
  done: boolean;
  sessions: { id: string; title: string; status: string; replies: number }[];
}

export interface CardDetail {
  node: { id: string; text: string; note: string };
  children: { id: string; text: string; note: string; done: boolean }[];
  sessions: { id: string; title: string; status: string;
              messages: { id: string; role: string; kind: string; content: string;
                          author: string; toolName: string }[] }[];
}

const esc = (v: string) =>
  v.replace(/[&<>"]/g, (ch) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[ch]!);

/** 1 枚のカード。返信の件数は**ターン数ではなく発言の数**（Mac の「n 件の返信」と同じ数え方）。 */
function cardHTML(card: BoardCard): string {
  const replies = card.sessions.reduce((n, s) => n + s.replies, 0);
  const running = card.sessions.some((s) => s.status === "running");
  return `
    <button class="card${card.done ? " done" : ""}" data-card="${esc(card.id)}">
      <span class="card-text">${esc(card.text) || "（無題）"}</span>
      ${card.note ? `<span class="card-note">${esc(card.note.split("\n")[0]!)}</span>` : ""}
      ${replies > 0 || running
        ? `<span class="card-meta">${running ? '<i class="dot running"></i>' : ""}${
            replies > 0 ? `${replies} 件の返信` : "作業中"
          }</span>`
        : ""}
    </button>`;
}

export function boardHTML(columns: BoardColumn[], cards: BoardCard[]): string {
  if (columns.length === 0) {
    return `<p class="empty">まだボードがありません。</p>`;
  }
  return columns
    .map((col) => {
      const mine = cards.filter((c) => c.statusId === col.id);
      return `
        <section class="column">
          <header class="column-head">
            <span class="column-name">${esc(col.name)}</span>
            <span class="column-count">${mine.length}</span>
          </header>
          <div class="column-body">
            ${mine.map(cardHTML).join("") || `<p class="column-empty">まだ何もありません</p>`}
          </div>
        </section>`;
    })
    .join("");
}

/** カードの中身。**会話はここで初めて読み込む。** */
export function detailHTML(detail: CardDetail): string {
  const body = detail.children.length
    ? `<ul class="detail-note">${detail.children
        .map((c) => `<li class="${c.done ? "done" : ""}">${esc(c.text)}${
          c.note ? `<span class="child-note">${esc(c.note)}</span>` : ""}</li>`)
        .join("")}</ul>`
    : `<p class="muted">中身はまだありません。</p>`;

  const talk = detail.sessions.length
    ? detail.sessions
        .map(
          (s) => `
            <section class="talk">
              <h3>${esc(s.title) || "AI への依頼"}</h3>
              ${s.messages
                .filter((m) => m.kind === "text" && m.content.trim() !== "")
                .map(
                  (m) => `
                    <div class="chat-msg ${m.role === "user" ? "from-user" : "from-ai"}">
                      <span class="chat-who">${m.role === "user" ? esc(m.author || "依頼") : "Claude"}</span>
                      <div class="body">${esc(m.content)}</div>
                    </div>`,
                )
                .join("")}
            </section>`,
        )
        .join("")
    : "";

  return `
    <header class="detail-head">
      <h2>${esc(detail.node.text) || "（無題）"}</h2>
      <button class="close" data-close="1">閉じる</button>
    </header>
    ${body}
    ${talk}`;
}
